import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Send, Clock, Target } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function FollowUpAutomation() {
  return (
    <>
      <Helmet>
        <title>Follow-Up Automation - Never Miss a Follow-Up | LoopBaan.ai</title>
        <meta name="description" content="Automatically send perfectly timed follow-up emails to hiring managers. Increase your response rate and land more interviews." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-accent/10 via-background to-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-accent/20 text-accent border-accent/30">AUTOMATION</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Follow-Up Emails That{" "}
                <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                  Get Responses
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Stop wondering when to follow up. Our AI sends perfectly timed, personalized emails that keep you top-of-mind with hiring managers.
              </p>
              <div className="flex justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Start Automating <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Clock,
                  title: "Perfect Timing",
                  description: "AI determines the optimal time to send follow-ups based on industry standards and response patterns."
                },
                {
                  icon: Target,
                  title: "Personalized Messages",
                  description: "Every follow-up is tailored to the specific job, company, and stage of your application."
                },
                {
                  icon: Send,
                  title: "Automatic Sending",
                  description: "Set it and forget it. Follow-ups are sent automatically without any manual work from you."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">How It Works</h2>
              <div className="space-y-8">
                {[
                  {
                    step: "1",
                    title: "Apply to Jobs",
                    description: "Use our platform to apply to jobs or import your existing applications from any source."
                  },
                  {
                    step: "2",
                    title: "AI Plans Follow-Ups",
                    description: "Our AI analyzes each application and creates a customized follow-up schedule based on best practices."
                  },
                  {
                    step: "3",
                    title: "Automatic Sending",
                    description: "Follow-up emails are sent automatically at the perfect time to maximize your chances of getting a response."
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center text-accent-foreground font-bold text-xl">
                        {item.step}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-lg">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Why Follow-Up Matters</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "80% of sales require 5 follow-ups to close",
                  "Following up shows initiative and interest",
                  "Keeps your name top-of-mind",
                  "Demonstrates professionalism",
                  "Increases response rate by 50%",
                  "Separates you from other candidates",
                  "Shows you're serious about the role",
                  "Creates opportunities for conversation"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Never Miss a Follow-Up Again
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let AI handle your follow-ups while you focus on preparing for interviews.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Get Started <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
